import { el } from 'date-fns/locale'
import { ChatFlowHandler, Question, Repo } from '../types'
import {
  generateHeroVariants,
  generateContent,
  analyzeQuestions,
  sectionPrompts,
  editSection
} from './api'
import {
  savePageMessage,
  savePageMessageToThread,
  updatePageContext
} from '@/lib/message-service'
import { callWalterAPI } from './api'
import {
  BrandRepositoryResponse,
  fetchBrandRepositories
} from '@/lib/brand-repo-services'
import {
  CREDIT_COSTS,
  Feature
} from '@/app/(main)/ai-design-center/lib/constants'

interface UIBuilderState {
  userPrompt: string
  questionsHistory: Question[]
  currentQuestion: string | null
  currentChoices: string[] | null
  conversationComplete: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  analyzedData: any
  // Multi-page website support
  currentPage: string
  availablePages: string[]
  generatedPages: Record<string, string> // Map of page_name -> HTML content
  selectedHero: string | null
  selectedRepo: { id: string; name: string } | null
  
  // Edit mode support
  isEditMode: boolean
  isEditModeEnabled?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pageDetails?: any
  previousSections: string[]
  editQuestions: Question[]
  currentEditRequest: string | null
  currentContent: string | null
  originalPrompt: string | null
  // Add website type
  websiteType: 'landing' | 'single' | 'multi' | null
  promptFromUrl?: string | null
  navFlag?: boolean | null
  isRepoSelected: boolean;
}

const extractMaxThreeColors = (repo: BrandRepositoryResponse) => {
  const primary = (repo.styleAssets?.colors?.primary as string[]) ?? []
  const secondary = (repo.styleAssets?.colors?.secondary as string[]) ?? []

  const combined = [...primary, ...secondary]
  return combined
}

// Helper function to update section in content
const updateSectionInContent = (
  fullContent: string,
  sectionId: string,
  sectionData: {
    html: string
    styles: string
    scripts: string
  }
): string => {
  if (!fullContent || !sectionId || !sectionData.html) return fullContent

  try {
    // Create a DOM parser to work with the HTML content
    const parser = new DOMParser()
    const doc = parser.parseFromString(fullContent, 'text/html')

    // Find the section by ID
    const sectionToReplace = doc.getElementById(sectionId)
    if (!sectionToReplace) {
      console.warn(`Section with ID "${sectionId}" not found in the content`)
      return fullContent
    }

    // Parse the new section HTML
    const tempContainer = document.createElement('div')
    tempContainer.innerHTML = sectionData.html.trim()
    const newSection = tempContainer.firstElementChild

    if (!newSection) {
      console.warn('New section HTML is empty or invalid')
      return fullContent
    }

    // Replace the old section with the new one
    sectionToReplace.parentNode?.replaceChild(
      newSection.cloneNode(true),
      sectionToReplace
    )

    // Handle styles - add them to the head if not already present
    if (sectionData.styles && sectionData.styles.trim()) {
      // Check if we already have a style tag with similar content
      let styleExists = false
      const styleId = `section-${sectionId}-styles`
      let styleTag = doc.getElementById(styleId)

      if (styleTag) {
        // Update existing style tag
        styleTag.textContent = sectionData.styles
        styleExists = true
      }

      if (!styleExists) {
        // Create a new style tag
        styleTag = doc.createElement('style')
        styleTag.id = styleId
        styleTag.textContent = sectionData.styles

        // Add it to the head
        const head = doc.querySelector('head')
        if (head) {
          head.appendChild(styleTag)
        }
      }
    }

    // Return the updated HTML content
    return new XMLSerializer().serializeToString(doc)
  } catch (error) {
    console.error('Error updating section in content:', error)
    return fullContent
  }
}

export const defaultChatFlow: ChatFlowHandler = {
  getInitialMessages: initialData => {
    if (initialData?.promptFromUrl) {
      return [
        // { text: initialData.promptFromUrl, sender: 'user' },
        // { text: 'Processing your request...', sender: 'bot' }
      ]
    }
    // If there's already a user prompt, return those messages
    if (initialData?.user_prompt) {
      return [
        { text: initialData.user_prompt, sender: 'user' },
        { text: initialData.appreciation, sender: 'bot' }
      ]
    }

    if (initialData?.isEditModeEnabled) {
      return [
        {
          text: "Hi, I'm Walter! I'm here to help you edit your website. Please tell me what changes you'd like to make to your page.",
          sender: 'bot',
          choices: [
            { id: 1, text: 'Edit Content' },
            { id: 2, text: 'Change Layout' },
            { id: 3, text: 'Update Styling' }
          ]
        }
      ]
    }

    // Otherwise, return the initial website type selection message
    return [
      {
        text: 'Hey, I am Walter. I am here to help you. What would you like to create?',
        sender: 'bot',
        choices: [
          {
            id: 1,
            text: 'Landing Page',
            description:
              'A conversion-focused page designed for a specific marketing campaign or offer. Ideal for capturing leads or driving sales with a single, clear call to action (CTA).',
            tooltip: `Cost: ${CREDIT_COSTS[Feature.WEB_PAGE_GENERATION_LANDING]} credits`
          },
          {
            id: 2,
            text: 'Single Page',
            description:
              'A complete website where all content exists on one long, scrolling page. Best for portfolios, simple business overviews, or focused narratives that flow from top to bottom.',
            tooltip: `Cost: ${CREDIT_COSTS[Feature.WEB_PAGE_GENERATION]} credits`
          },
          {
            id: 3,
            text: 'Multi Page',
            description:
              'A traditional website structure with several distinct pages (e.g., Home, About, Services, Contact) connected by a navigation menu. Ideal for organizing larger amounts of diverse content.',
            tooltip: `Cost: ${CREDIT_COSTS[Feature.WEB_PAGE_GENERATION_MULTI]} credits`
          }
        ]
      }
    ]
  },

  getInitialState: initialData => {
    return {
      userPrompt: initialData?.user_prompt || '',
      questionsHistory: [],
      currentQuestion: initialData?.question || null,
      currentChoices: initialData?.choices || null,
      conversationComplete: !initialData?.question || false,
      analyzedData: initialData || {},
      // Multi-page website support
      currentPage: 'home',
      availablePages: [],
      generatedPages: {},
      selectedHero: null,
      selectedRepo: null,
      // Edit mode support
      isEditMode: false,
      isEditModeEnabled: initialData?.isEditModeEnabled || false,
      pageDetails: initialData?.pageDetails || {},
      previousSections: [],
      editQuestions: [],
      currentEditRequest: null,
      currentContent: null,
      originalPrompt: null,
      websiteType: null,
      promptFromUrl: initialData?.promptFromUrl || null, // Add this line
      navFlag: false,
      isRepoSelected: false
    } as UIBuilderState
  },

  initialize: async (state: UIBuilderState, sendMessage) => {
    if (!state.websiteType) {
      await sendMessage(
        'Hey, I am Walter. I am here to help you build your page. What would you like to create?',
        {
          choices: [
            {
              id: 1,
              text: 'Landing Page',
              description:
                'A conversion-focused page designed for a specific marketing campaign or offer. Ideal for capturing leads or driving sales with a single, clear call to action (CTA). Landing pages do not include page navigation.'
            },
            {
              id: 2,
              text: 'Single Page',
              description:
                'A complete website where all content exists on one long, scrolling page. Best for portfolios, simple business overviews, or focused narratives that flow from top to bottom.'
            },
            {
              id: 3,
              text: 'Multi Page',
              description:
                'A traditional website structure with several distinct pages (e.g., Home, About, Services, Contact) connected by a navigation menu. Ideal for organizing larger amounts of diverse content.'
            }
          ]
        }
      )
    } else if (state.currentQuestion) {
      await sendMessage(state.currentQuestion)
    }
  },

  analyzePrompt: async (state: UIBuilderState) => {
    // Call analyzeQuestions API to kick off the flow
    const analyzedData = await analyzeQuestions({
      user_prompt: state.userPrompt,
      questions: [],
      website_type: state.websiteType || '',
      brandkit_id: state.selectedRepo?.id
    })

    return analyzedData
  },

  handleUserInput: async (
    input,
    state: UIBuilderState,
    sendMessage,
    initialData,
    activeThreadId,
    setIsTyping
  ) => {
    const newState = { ...state }
    if (
      !newState.websiteType &&
      !state.isEditModeEnabled &&
      !state.promptFromUrl
    ) {
      const lowerInput = input.toLowerCase().trim()
      if (lowerInput.includes('landing') || lowerInput === '1') {
        newState.websiteType = 'landing'
        newState.availablePages = [] // Ensure no additional pages are available
        // Send hardcoded appreciation and question

        // await savePageMessage(
        //   initialData.pageDetails.landingPageId,
        //   'user',
        //   input,
        //   initialData.isEditModeEnabled
        // )
        // if (initialData.pageDetails.landingPageId) {
        //   await savePageMessageToThread(
        //     initialData.pageDetails.landingPageId,
        //     activeThreadId,
        //     'user',
        //     input,
        //     initialData.isEditModeEnabled
        //   )
        // }
        await sendMessage(
          'Perfect choice! A landing page is great for making a strong first impression.'
        )

        try {
          setIsTyping(true)
          const repositroies = await fetchBrandRepositories()
          if (repositroies?.length > 0) {
            const repoChoices = repositroies.map((repo, index) => ({
              id: repo.id,
              name: repo?.brandName,
              colors: extractMaxThreeColors(repo)
            }))

            await sendMessage(
              'We found these brand kit repositories. Please select one if you want to use its data to create your website:',
              { repos: repoChoices }
            )
          } else {
            await sendMessage(
              'Now, tell me about your business or project - what would you like to create? Feel free to share your vision and goals.'
            )
          }
        } catch (err) {
          console.error(err)
        } finally {
          setIsTyping(false)
        }
      } else if (lowerInput.includes('single') || lowerInput === '2') {
        newState.websiteType = 'single'
        newState.availablePages = []
        // if (initialData.pageDetails.landingPageId) {
        //   await savePageMessageToThread(
        //     initialData.pageDetails.landingPageId,
        //     activeThreadId,
        //     'user',
        //     input,
        //     initialData.isEditModeEnabled
        //   )
        // }
        // Send hardcoded appreciation and question
        await sendMessage(
          'Great choice! A single-page website is perfect for creating an immersive, flowing experience.'
        )

        try {
          setIsTyping(true)
          const repositroies = await fetchBrandRepositories()
          if (repositroies?.length > 0) {
            const repoChoices = repositroies.map(repo => ({
              id: repo.id,
              name: repo?.brandName,
              colors: extractMaxThreeColors(repo)
            }))

            await sendMessage(
              'We found these brand kit repositories. Please select one if you want to use its data to create your website:',
              { repos: repoChoices }
            )
          } else {
            await sendMessage(
              'Now, tell me about your business or project - what would you like to create? Feel free to share your vision and goals.'
            )
          }
        } catch (err) {
          console.error(err)
        } finally {
          setIsTyping(false)
        }
      } else if (lowerInput.includes('multi') || lowerInput === '3') {
        newState.websiteType = 'multi'
        // if (initialData.pageDetails.landingPageId) {
        //   await savePageMessageToThread(
        //     initialData.pageDetails.landingPageId,
        //     activeThreadId,
        //     'user',
        //     input,
        //     initialData.isEditModeEnabled
        //   )
        // }

        await sendMessage(
          'Perfect choice! A multi-page website will help organize your content effectively.'
        )
        try {
          setIsTyping(true)
          const repositroies = await fetchBrandRepositories()
          if (repositroies?.length > 0) {
            const repoChoices = repositroies.map((repo, index) => ({
              id: repo.id,
              name: repo?.brandName,
              colors: extractMaxThreeColors(repo)
            }))

            await sendMessage(
              'We found these brand kit repositories. Please select one if you want to use its data to create your website:',
              { repos: repoChoices }
            )
          } else {
            await sendMessage(
              'Now, tell me about your business or project - what would you like to create? Feel free to share your vision and goals.'
            )
          }
        } catch (err) {
          console.error(err)
        } finally {
          setIsTyping(false)
        }
      } else {
        const validWebsiteTypes = ['landing', 'single', 'multi']
        const validWebsiteNumbers = ['1', '2', '3']
        if (
          !validWebsiteTypes.some(type => lowerInput.includes(type)) &&
          !validWebsiteNumbers.includes(lowerInput)
        ) {
          try {
            const response = await callWalterAPI({ query: input })

            if (response?.type === 'navigation' && response?.status === 200) {
              // await sendMessage(
              //   response.message || 'Navigating to the requested page...'
              // )
              return { newState, navigateTo: response.result }
            } else if (
              response?.type === 'navigation' &&
              response?.status === 404
            ) {
              // await sendMessage(
              //   response.result || 'Sorry, the requested page was not found.'
              // )
            } else {
              await sendMessage(
                response?.message ||
                  'I didn’t understand your request. Please select "Landing Page" or "Website" to continue, or clarify your intent.'
              )
            }
          } catch (error) {
            console.error('Error processing request:', error)
            await sendMessage(
              'An error occurred while processing your request. Please select "Landing Page" or "Website" to continue.'
            )
          }
        } else {
          await sendMessage(
            'Please select either "Landing Page" or "Website" to continue.'
          )
        }
      }
      return { newState }
    } else if (state.isEditModeEnabled && !state.isEditMode) {
      // Handle edit mode choices
      const lowerInput = input.toLowerCase().trim()
      if (lowerInput.includes('content') || lowerInput === '1') {
        if (initialData.pageDetails.landingPageId) {
          await savePageMessageToThread(
            initialData.pageDetails.landingPageId,
            activeThreadId,
            'user',
            input,
            initialData.isEditModeEnabled
          )
        }
        await sendMessage(
          "I'll help you edit the content. Please tell me which section you'd like to modify and what changes you'd like to make."
        )
      } else if (lowerInput.includes('layout') || lowerInput === '2') {
        if (initialData.pageDetails.landingPageId) {
          await savePageMessageToThread(
            initialData.pageDetails.landingPageId,
            activeThreadId,
            'user',
            input,
            initialData.isEditModeEnabled
          )
        }
        await sendMessage(
          "I'll help you with the layout changes. Please tell me which section you'd like to modify and what changes you'd like to make."
        )
      } else if (lowerInput.includes('styling') || lowerInput === '3') {
        if (initialData.pageDetails.landingPageId) {
          await savePageMessageToThread(
            initialData.pageDetails.landingPageId,
            activeThreadId,
            'user',
            input,
            initialData.isEditModeEnabled
          )
        }
        await sendMessage(
          "I'll help you update the styling. Please tell me which section you'd like to modify and what changes you'd like to make."
        )
      }
      newState.isEditMode = true
      return { newState }
    }

    if (newState.isEditMode && !newState.originalPrompt) {
      newState.originalPrompt = newState.pageDetails.pageContext.originalPrompt
      newState.previousSections =
        newState.pageDetails.pageContext.sectionPrompts
      newState.currentContent = newState.pageDetails.pageContext.pageContent
    }

    if (newState.promptFromUrl) {
      newState.websiteType = 'single'
      newState.userPrompt = newState.promptFromUrl
    }

    // Handle the business description (second input)
    if (!newState.userPrompt) {
      newState.userPrompt = input
    }

    // If in edit mode, handle edit flow
    if (newState.isEditMode) {
      if (!newState.currentEditRequest) {
        newState.currentEditRequest = input
        await sendMessage(`Analyzing your request.`)
      } else if (newState.currentQuestion) {
        newState.editQuestions.push({
          q: newState.currentQuestion,
          a: input
        })
      }

      const response = await editSection({
        original_prompt:
          newState.originalPrompt || newState.analyzedData.user_prompt,
        edit_request: newState.currentEditRequest || input,
        previous_sections: newState.previousSections,
        questions: newState.editQuestions
      })

      if (response.type === 'navigation' && response.status === 200) {
        // await sendMessage(response.message)
        return { newState, navigateTo: response.result }
      } else if (response.type === 'navigation' && response.status === 404) {
        // await sendMessage(response.result)
        return { newState }
      }

      if (response.appreciation) {
        // if (initialData.pageDetails.landingPageId) {
        //   await savePageMessageToThread(
        //     initialData.pageDetails.landingPageId,
        //     activeThreadId,
        //     'user',
        //     input,
        //     initialData.isEditModeEnabled
        //   )
        // }
        await sendMessage(response.appreciation)
      }

      if (response.complete && response.section_identified) {
        // await savePageMessage(
        //   initialData.pageDetails.landingPageId,
        //   'user',
        //   input,
        //   initialData.isEditModeEnabled
        // )
        await sendMessage('Processing your edit request...')

        // Update the section prompt in our stored prompts
        const updatedSectionPrompts = [...newState.previousSections]
        const sectionIndex = newState.previousSections.findIndex(section =>
          section
            .toLowerCase()
            .includes(response.section_identified?.toLowerCase() || '')
        )

        if (sectionIndex >= 0 && response.enhanced_prompt) {
          updatedSectionPrompts[sectionIndex] = response.enhanced_prompt
        } else if (response.enhanced_prompt) {
          updatedSectionPrompts.push(response.enhanced_prompt)
        }

        newState.previousSections = updatedSectionPrompts

        let updatedContent = newState.currentContent || ''

        if (response.section_html && response.section_identified) {
          const cleanHtml = response.section_html.html.replace(
            /^\s*[\r\n]/gm,
            ''
          )

          updatedContent = updateSectionInContent(
            newState.currentContent || '',
            response.section_identified,
            {
              html: cleanHtml,
              styles: response.section_html.styles || '',
              scripts: response.section_html.scripts || ''
            }
          )
        } else {
          updatedContent = await generateContent({
            prompts: updatedSectionPrompts,
            selected_hero: newState.selectedHero || '',
            page_name: newState.currentPage,
            website_type: newState.websiteType ?? undefined,
            page_id: newState.pageDetails?.pageId || null
          })
        }

        newState.currentQuestion = null
        newState.currentEditRequest = null
        newState.editQuestions = []
        newState.currentContent = updatedContent
        newState.generatedPages[newState.currentPage] = updatedContent

        if (newState.pageDetails?.pageId) {
          const pageUpdate = await updatePageContext(
            newState.pageDetails.pageId,
            newState.analyzedData.user_prompt,
            newState.previousSections,
            newState.currentContent
          )
        }

        await sendMessage(
          "I've updated the content based on your edit request. You can now view the revised website!"
        )

        return {
          newState,
          content: updatedContent,
          sectionScripts: response.section_html?.scripts || ''
        }
      } else if (response.next_question) {
        newState.currentQuestion = response.next_question
        newState.currentChoices = response.choices || null
        await sendMessage(response.next_question, {
          choices: response.choices // Pass the choices to the message
        })
      }

      return { newState }
    }

    // Check for page generation request
    if (
      newState.conversationComplete &&
      newState.websiteType === 'multi' &&
      newState.availablePages.length > 0
    ) {
      const lowerInput = input.toLowerCase().trim()

      const matchedPage = newState.availablePages.find(
        page =>
          page.toLowerCase() === lowerInput ||
          `generate ${page} page`.toLowerCase() === lowerInput ||
          `create ${page} page`.toLowerCase() === lowerInput
      )

      if (
        matchedPage &&
        !newState.generatedPages[matchedPage] &&
        defaultChatFlow.generateAdditionalPage
      ) {
        await sendMessage(
          `I'll generate the ${matchedPage} page for you. Please wait...`
        )
        const result = await defaultChatFlow.generateAdditionalPage(
          matchedPage,
          newState,
          sendMessage
          // setStreamingProgress,
          // setIsStreaming
        )

        if (result.content) {
          newState.currentContent = result.content
          newState.currentPage = matchedPage
          newState.isEditMode = true // Enable edit mode after generating new page
        }

        return {
          newState: result.newState || newState,
          content: result.content,
          variants: null
        }
      }
    }

    // Normal question-answer flow
    if (newState.currentQuestion) {
      newState.questionsHistory = [
        ...newState.questionsHistory,
        { q: newState.currentQuestion, a: input }
      ]
    }

    if (newState.navFlag) {
      newState.questionsHistory = []
      newState.userPrompt = input
      // newState.navFlag = false
    }

    const analyzedData = await analyzeQuestions({
      user_prompt: newState.userPrompt,
      questions: newState.questionsHistory,
      website_type: state.websiteType || '',
      brandkit_id: newState.selectedRepo?.id ?? ''
    })

    if (analyzedData.type === 'navigation' && analyzedData.status === 200) {
      // await sendMessage(analyzedData.message)
      return { newState, navigateTo: analyzedData.result }
    } else if (
      analyzedData.type === 'navigation' &&
      analyzedData.status === 404
    ) {
      // await sendMessage(analyzedData.result)
      return { newState }
    }

    newState.analyzedData = analyzedData

    if (analyzedData.appreciation) {
      // if (initialData.pageDetails.landingPageId) {
      //   await savePageMessageToThread(
      //     initialData.pageDetails.landingPageId,
      //     activeThreadId,
      //     'user',
      //     input,
      //     initialData.isEditModeEnabled
      //   )
      // }
      await sendMessage(analyzedData.appreciation)
      // await sendMessage(analyzedData.appreciation)
    }

    newState.currentQuestion = analyzedData.question || null
    newState.currentChoices = analyzedData.choices || null

    if (analyzedData.question) {
      // await savePageMessage(
      //   initialData.pageDetails.landingPageId,
      //   'user',
      //   input,
      //   initialData.isEditModeEnabled
      // )
      await sendMessage(analyzedData.question, {
        choices: analyzedData.choices // Pass the choices to the message
      })
    } else {
      if (newState.pageDetails?.pageId) {
        await updatePageContext(
          newState.pageDetails.pageId,
          newState.analyzedData.user_prompt,
          newState.previousSections,
          newState.currentContent || ''
        )
      }
      newState.conversationComplete = true
      // newState.questionsHistory = []
      newState.navFlag = true

      await sendMessage(
        'Generating Some Variants about your provided information...'
      )
      const { variants } = await generateHeroVariants({
        prompt: analyzedData.user_prompt
      })

      await sendMessage(
        "I've generated 4 variants. Please select the one you prefer:",
        { showVariants: true, variants }
      )

      if (analyzedData.type === 'navigation' && analyzedData.status == 200) {
        // await sendMessage(analyzedData.message)
        return { newState, variants, navigateTo: analyzedData.result }
      } else if (
        analyzedData.type === 'navigation' &&
        analyzedData.status == 404
      ) {
        // await sendMessage(analyzedData.result)
        return { newState, variants }
      }

      return {
        newState,
        variants
      }
    }

    return { newState }
  },

  generateVariants: async (state: UIBuilderState) => {
    const { variants } = await generateHeroVariants({
      prompt: state.analyzedData.user_prompt
    })
    return { variants }
  },

  handleRepoSelection: async (repo, state: UIBuilderState, sendMessage) => {
    const newState = { ...state }
    if (repo) {
      newState.selectedRepo = repo
      await sendMessage(`You've selected repo: ${repo.name}`)
    } else {
      await sendMessage(
        `No repository selected. Proceeding without brand context.`
      )
    }

    newState.isRepoSelected = true;
    

    await sendMessage(
      'Great! Now, tell me about your business or project - what would you like to create? Feel free to share your vision and goals.'
    )

    

    return {
      closeChat: false,
      newState
    }
  },

  handleVariantSelection: async (
    variant,
    state: UIBuilderState,
    sendMessage,
    setStreamingProgress,
    setIsStreaming,
    setIsGeneratingSectionPrompts
  ) => {
    const newState = { ...state }
    newState.selectedHero = variant.html
    newState.originalPrompt = newState.analyzedData.user_prompt

    await sendMessage(
      `You've selected variant ${variant.id}: ${variant.description}`
    )
    if (state.websiteType === 'landing') {
      await sendMessage('Generating the landing page...')
    } else {
      await sendMessage('Generating the home page for your website...')
    }
    
    // Set streaming to true and progress to 10% before section prompts API call
    setIsStreaming?.(true)
    setStreamingProgress?.(10)
    
    // Generate home page first
    const sectionPromptsResponse = await sectionPrompts({
      user_prompt: newState.originalPrompt || newState.userPrompt,
      selected_hero: variant.html,
      page_name: 'home',
      website_type: state.websiteType,
      brandkit_id: newState.selectedRepo?.id ?? ''
    })
    
    // Only save available pages if it's a multi-page website
    newState.availablePages =
      newState.websiteType === 'multi'
        ? sectionPromptsResponse.available_pages || []
        : []

    // Save available pages for further generation
    // newState.availablePages = sectionPromptsResponse.available_pages || []
    newState.previousSections = sectionPromptsResponse.prompts
    if (newState.pageDetails?.pageId) {
      await updatePageContext(
        newState.pageDetails.pageId,
        newState.analyzedData.user_prompt ||
          newState.pageDetails.pageContext.originalPrompt,
        newState.previousSections,
        newState.currentContent || ''
      )
    }

    // Generate the home page content
    const homeContent = await generateContent(
      {
        prompts: sectionPromptsResponse.prompts,
        selected_hero: variant.html,
        page_name: 'home',
        website_type: newState.websiteType ?? undefined,
        page_id: newState.pageDetails?.pageId || null
      },
      {
        onProgress: percentage => {
          // Scale the remaining 90% of progress
          const scaledPercentage = Math.round(10 + (percentage * 0.9));
          setStreamingProgress?.(scaledPercentage)
        },
        onComplete: () => {
          setTimeout(() => {
            setIsStreaming?.(false)
            setStreamingProgress?.(100)
          }, 500) // Small delay to ensure 100% is shown briefly
        }
      }
    )

    console.log({homeContent: JSON.stringify(homeContent)})

    // Save the generated home page
    newState.generatedPages['home'] = homeContent
    newState.currentContent = homeContent
    newState.currentPage = 'home'
    newState.isEditMode = true // Enable edit mode after generating home page
    if (newState.pageDetails?.pageId) {
      await updatePageContext(
        newState.pageDetails.pageId,
        newState.analyzedData.user_prompt ||
          newState.pageDetails.pageContext.originalPrompt,
        newState.previousSections,
        newState.currentContent
      )
    }
    // Inform user about what's been generated and what can be generated next
    if (state.websiteType === 'landing') {
      await sendMessage(
        `I've generated the landing page according to your requirements!`
      )
      await sendMessage(
        `Would you like me to assist in editing your webpage? Please go ahead with your request.`
      )
    } else {
      await sendMessage(
        `I've generated the home page for your ${state.websiteType}-page site!`
      )
      if(state.websiteType === 'single') {
        await sendMessage(
          `Would you like me to assist in editing your webpage? Please go ahead with your request.`
        )
      }
    }

    // Only show additional pages message for multi-page websites
    if (
      newState.websiteType === 'multi' &&
      newState.availablePages.length > 0
    ) {
      await sendMessage(
        `I can also create these additional pages for your website: ${newState.availablePages.join(', ')}. ` +
          `Just ask me to "generate the [page name] page" whenever you're ready.`
      )
    }

    return {
      content: homeContent,
      newState,
      closeChat: false
    }
  },

  generateAdditionalPage: async (
    pageName: string,
    state: UIBuilderState,
    sendMessage,
    setStreamingProgress,
    setIsStreaming
  ) => {
    const newState = { ...state }

    try {
      // Start streaming progress at 10% during section prompts generation
      setIsStreaming?.(true)
      setStreamingProgress?.(10)
      
      // Generate the specific page content
      const pagePromptsResponse = await sectionPrompts({
        user_prompt:
          newState.originalPrompt ||
          newState.analyzedData.user_prompt ||
          newState.pageDetails?.pageContext?.originalPrompt ||
          'default prompt',
        selected_hero: newState.selectedHero || '',
        page_name: pageName,
        website_type: state.websiteType
      })

      // Don't reset streaming progress - continue from 10%
      const pageContent = await generateContent(
        {
          prompts: pagePromptsResponse.prompts,
          selected_hero: newState.selectedHero || '',
          page_name: pageName,
          website_type: newState.websiteType ?? undefined,
          page_id: newState.pageDetails?.pageId || null
        },
        {
          onProgress: percentage => {
            // Scale the remaining 90% of progress
            const scaledPercentage = Math.round(10 + (percentage * 0.9));
            setStreamingProgress?.(scaledPercentage)
          },
          onComplete: () => {
            setTimeout(() => {
              setIsStreaming?.(false)
              setStreamingProgress?.(100)
            }, 500) // Small delay to ensure 100% is shown briefly
          }
        }
      )
  
      // Save the generated page content to the newly created page
      if (newState.pageDetails?.pageId) {
        await updatePageContext(
          newState.pageDetails.pageId,
          newState.originalPrompt || newState.analyzedData.user_prompt,
          pagePromptsResponse.prompts,
          pageContent
        )
      }
  
      // Save the generated page
      newState.generatedPages[pageName] = pageContent
      newState.currentPage = pageName
      newState.currentContent = pageContent
      newState.previousSections = pagePromptsResponse.prompts
      newState.isEditMode = true // Enable edit mode for the new page

      // Remove from available pages
      newState.availablePages = newState.availablePages.filter(
        p => p !== pageName
      )

      await sendMessage(`I've generated the ${pageName} page for your website!`)

      if (newState.availablePages.length > 0) {
        await sendMessage(
          `I can still create these additional pages: ${newState.availablePages.join(', ')}. ` +
            `Just let me know which one you'd like next.`
        )
      } else {
        await sendMessage('That completes all the pages for your website!')
        await sendMessage(
          `Would you like me to assist in editing your webpage? Please go ahead with your request.`
        )
      }

      return {
        content: pageContent,
        newState,
        closeChat: false
      }
    } catch (error) {
      console.error(`Error generating ${pageName} page:`, error)
      await sendMessage(
        `I encountered an error while generating the ${pageName} page. Please try again.`
      )
      return { newState }
    }
  }
}
